import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Configurar interceptor de Axios para enviar token en header (Safari compatibility)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para limpiar token en caso de 401
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('session_token');
    }
    return Promise.reject(error);
  }
);

import HomePage from '@/pages/HomePage';
import LigasLoginPage from '@/pages/LigasLoginPage';
import AcademiasLoginPage from '@/pages/AcademiasLoginPage';
import ClubLoginPage from '@/pages/ClubLoginPage';
import RegistroPage from '@/pages/RegistroPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InscripcionPage from '@/pages/InscripcionPage';
import InscripcionAcademiaPage from '@/pages/InscripcionAcademiaPage';
import InvitacionEntrenadorPage from '@/pages/InvitacionEntrenadorPage';
import EntrenadorLoginPage from '@/pages/EntrenadorLoginPage';
import EntrenadorDashboard from '@/pages/EntrenadorDashboard';
import ConsultaPage from '@/pages/ConsultaPage';
import LandingPage from '@/pages/LandingPage';
import AdminDashboard from '@/pages/AdminDashboard';
import TurnoDashboard from '@/pages/TurnoDashboard';
import IntegranteDashboard from '@/pages/IntegranteDashboard';
import ClubDashboard from '@/pages/ClubDashboard';
import AcademiaDashboard from '@/pages/AcademiaDashboard';
import PublicView from '@/pages/PublicView';
import MatchDetailView from '@/pages/MatchDetailView';
import StaffAcademiasDashboard from '@/pages/StaffAcademiasDashboard';
import AcademiaConsultaPage from '@/pages/AcademiaConsultaPage';
import TerminosPage from '@/pages/TerminosPage';
import PrivacidadPage from '@/pages/PrivacidadPage';
import BackofficePage from '@/pages/BackofficePage';
import EnrolamientoPage from '@/pages/EnrolamientoPage';
import TorneoPublicoPage from '@/pages/TorneoPublicoPage';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// AuthCallback component to handle session_id from Google OAuth
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          window.location.hash = '';
          
          // Determine redirect path based on current pathname
          const pathname = location.pathname;
          let redirectPath = '/ligas/dashboard';
          if (pathname.includes('/academias')) {
            redirectPath = '/academias/dashboard';
          } else if (pathname.includes('/club')) {
            redirectPath = '/club/dashboard';
          }
          
          // Use window.location.replace to prevent back button issues
          window.location.replace(redirectPath);
        } catch (error) {
          console.error('Error processing session:', error);
          navigate('/');
        }
      }
    };

    processSession();
  }, [navigate, location]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Autenticando...</p>
      </div>
    </div>
  );
}

function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check for session_id in URL hash
  const hasSessionId = location.hash?.includes('session_id=');

  useEffect(() => {
    // Only check session if we don't have a session_id in the hash
    if (!hasSessionId) {
      checkExistingSession();
    }
  }, [hasSessionId]);

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // No session
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth callback - show AuthCallback component
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (hasSessionId) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing Principal */}
      <Route path="/" element={<HomePage />} />
      
      {/* LIGAS - Rutas */}
      <Route path="/ligas" element={<LigasLoginPage />} />
      <Route path="/ligas/landing" element={user ? <Navigate to="/ligas/dashboard" /> : <LandingPage />} />
      <Route
        path="/ligas/dashboard"
        element={
          user ? (
            user.role === 'turno' ? <TurnoDashboard user={user} /> : <AdminDashboard user={user} />
          ) : (
            <Navigate to="/ligas" />
          )
        }
      />
      
      {/* ACADEMIAS - Rutas */}
      <Route path="/academias" element={<AcademiasLoginPage />} />
      <Route
        path="/academias/dashboard"
        element={
          user ? (
            user.role === 'integrante' ? <IntegranteDashboard user={user} /> : 
            user.role === 'turno' ? <StaffAcademiasDashboard user={user} /> : 
            <AcademiaDashboard />
          ) : (
            <Navigate to="/academias" />
          )
        }
      />
      
      {/* CLUB - Rutas */}
      <Route path="/club" element={<ClubLoginPage />} />
      <Route
        path="/club/dashboard"
        element={
          user ? (
            user.role === 'integrante' ? <IntegranteDashboard user={user} /> : <ClubDashboard user={user} />
          ) : (
            <Navigate to="/club" />
          )
        }
      />
      
      {/* Rutas Públicas (Ligas) */}
      <Route path="/public/:fechaId" element={<PublicView />} />
      <Route path="/public/partido/:partidoId" element={<MatchDetailView />} />
      
      {/* Registro por Invitación */}
      <Route path="/registro" element={<RegistroPage />} />
      
      {/* Auto-inscripción Pública */}
      <Route path="/inscripcion/:token" element={<InscripcionPage />} />
      <Route path="/academia/inscripcion/:token" element={<InscripcionAcademiaPage />} />
      
      {/* Invitación Entrenador Academia */}
      <Route path="/academia/invitacion/:token" element={<InvitacionEntrenadorPage />} />
      
      {/* Login y Dashboard Entrenador */}
      <Route path="/academia/:academiaId/login" element={<EntrenadorLoginPage />} />
      <Route path="/academia/:academiaId/entrenador" element={<EntrenadorDashboard />} />
      
      {/* Portal de Consulta para Alumnos de Academia */}
      <Route path="/academia/:academiaId/consulta" element={<AcademiaConsultaPage />} />
      
      {/* Portal de Consulta para Integrantes */}
      <Route path="/consulta/:clubId" element={<ConsultaPage />} />
      
      {/* Recuperación de Contraseña */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/restablecer-password" element={<ResetPasswordPage />} />
      
      {/* Páginas Legales */}
      <Route path="/terminos" element={<TerminosPage />} />
      <Route path="/privacidad" element={<PrivacidadPage />} />
      
      {/* Enrolamiento público */}
      <Route path="/enrolamiento" element={<EnrolamientoPage />} />
      <Route path="/torneo/:torneoId" element={<TorneoPublicoPage />} />
      
      {/* Backoffice Kinosis */}
      <Route path="/backoffice" element={<BackofficePage />} />
      
      {/* Legacy - Redirect old routes */}
      <Route path="/dashboard" element={<Navigate to="/ligas/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthHandler />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;